import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import Logo from "../components/Logo";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success(`Willkommen, ${user.name}`);
            navigate(user.role === "admin" ? "/admin" : "/employee", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-[#F8FAFC]" data-testid="login-page">
            {/* Left brand panel */}
            <div className="hidden lg:flex w-1/2 bg-[#1A365D] text-white relative overflow-hidden flex-col justify-between p-12">
                <div className="absolute -top-40 -right-32 w-[420px] h-[420px] rounded-full bg-[#16A34A]/25 blur-3xl" />
                <div className="absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full bg-white/5 blur-3xl" />
                <Link to="/" className="relative inline-flex items-center group" data-testid="login-home-link">
                    <Logo variant="dark" size="sm" />
                </Link>
                <div className="relative">
                    <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95] mb-6">
                        Mitarbeiter-Portal
                    </h1>
                    <p className="text-white/70 text-lg max-w-md leading-relaxed">
                        Verwalten Sie Aufträge, Kunden und Rechnungen — alles an einem Ort.
                    </p>
                </div>
                <div className="relative text-xs text-white/50 uppercase tracking-wider">© {new Date().getFullYear()} Privathilfe.ch</div>
            </div>

            {/* Right form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#64748B] hover:text-[#1A365D] mb-10" data-testid="login-back-link">
                        <ArrowLeft className="w-4 h-4" /> Zurück zur Website
                    </Link>
                    <div className="inline-flex w-14 h-14 rounded-2xl bg-[#1A365D] text-white items-center justify-center mb-5">
                        <Lock className="w-6 h-6" />
                    </div>
                    <h2 className="font-display font-black text-3xl text-[#1A365D] tracking-tight">Anmelden</h2>
                    <p className="text-sm text-[#64748B] mt-2 mb-8">Bitte melden Sie sich mit Ihren Zugangsdaten an.</p>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">E-Mail</label>
                            <input
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/15"
                                data-testid="login-email-input"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Passwort</label>
                            <input
                                required
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/15"
                                data-testid="login-password-input"
                                autoComplete="current-password"
                            />
                        </div>
                        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit-button">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Anmelden
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
